import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { AppDataProvider } from './contexts/AppDataContext';
import { NewsFeedProvider } from './contexts/NewsFeedContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/Layout/DashboardLayout';
import { colors } from './config/colors';

import Landing from './pages/Landing';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import CompanyDetails from './pages/CompanyDetails';
import MyCompanies from './pages/MyCompanies';
import MyTenantCompany from './pages/MyTenantCompany';
import Watchlist from './pages/Watchlist';
import News from './pages/News';
import Settings from './pages/Settings';

function App() {
  return (
    <AuthProvider>
      <LocaleProvider>
        <ThemeProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AppDataProvider>
              <div className="App">
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Navigate to="/login" replace />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />

                  <Route element={<DashboardLayout />}>
                    <Route
                      path="dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="companies"
                      element={
                        <ProtectedRoute>
                          <Companies />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="companies/:ticker"
                      element={
                        <ProtectedRoute>
                          <CompanyDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="my-companies"
                      element={
                        <ProtectedRoute>
                          <MyCompanies />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="my-tenant-company"
                      element={
                        <ProtectedRoute>
                          <MyTenantCompany />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="watchlist"
                      element={
                        <ProtectedRoute>
                          <Watchlist />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="watchlist-analysis" element={<Navigate to="/watchlist" replace />} />
                    <Route
                      path="news"
                      element={
                        <ProtectedRoute>
                          <NewsFeedProvider>
                            <News />
                          </NewsFeedProvider>
                        </ProtectedRoute>
                      }
                    />
                    <Route path="analytics" element={<Navigate to="/dashboard" replace />} />
                    <Route
                      path="settings"
                      element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      }
                    />
                  </Route>

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>

                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: colors.background.secondary,
                      color: colors.text.primary,
                      border: `1px solid ${colors.border.light}`,
                    },
                    success: {
                      duration: 3000,
                      iconTheme: {
                        primary: colors.success[500],
                        secondary: colors.background.primary,
                      },
                    },
                    error: {
                      duration: 5000,
                      iconTheme: {
                        primary: colors.danger[500],
                        secondary: colors.background.primary,
                      },
                    },
                  }}
                />
              </div>
            </AppDataProvider>
          </Router>
        </ThemeProvider>
      </LocaleProvider>
    </AuthProvider>
  );
}

export default App;
